import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Row, Col, Button, Modal } from "react-bootstrap";
import CountdownTimer from "../Components/CountdownTimer";
import ExamMcqQuestions from "../Components/ExamMcqQuestions";
import ExamQuestionNumbers from "../Components/ExamQuestionNumbers";
import axios from "axios";
import { FaCircleCheck } from "react-icons/fa6";

function ExamMcqPage() {
  const { state } = useLocation();
  const { assessment_name, questions, attempts, assessment_id } =
    state || {};
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answeredQuestions, setAnsweredQuestions] = useState({});
  const [userId, setUserId] = useState(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showWarning, setShowWarning] = useState(false);
  const navigate = useNavigate();
  const [score, setScore] = useState({ correctAnswers: 0, totalAnswered: 0 });
  const [loading, setLoading] = useState(true); // Added loading state here
  const [attemptTiming, setAttemptTiming] = useState(null);
  const [loadedQuestionCount, setLoadedQuestionCount] = useState(null);

  const totalQuestions = (loadedQuestionCount ?? Number(questions)) || 0;
  const answeredCount = Object.keys(answeredQuestions).length;
  const isAllAnswered =
    !loading && totalQuestions > 0 && answeredCount === totalQuestions;

  useEffect(() => {
    const storedUser = localStorage.getItem("user");
    if (storedUser) {
      try {
        const parsedUser = JSON.parse(storedUser);
        if (parsedUser && parsedUser.user_id) {
          setUserId(parsedUser.user_id);
        }
      } catch (error) {
        console.error("Error parsing user from localStorage:", error);
      }
    }
  }, []);

  // Prevent accidental refresh
  useEffect(() => {
    const handleBeforeUnload = (event) => {
      event.preventDefault();
      event.returnValue = "";
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, []);

  const handleSubmit = async (autoSubmit = false) => {
    if (!autoSubmit && !isAllAnswered) {
      return;
    }

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API}/submitAssessment`,
        {
          userId,
          assessmentId: assessment_id,
          attempt: attempts,
          allowIncomplete: autoSubmit,
        }
      );

      if (response.data.message === "Status updated successfully.") {
        setScore({
          correctAnswers: response.data.correctAnswers,
          totalAnswered: response.data.totalAnswered,
        });
        setShowSuccessModal(true);
      } else {
        alert(
          autoSubmit
            ? "Automatic submission failed. Please try again."
            : "Submission failed. Please try again."
        );
      }
    } catch (error) {
      console.error("Error submitting answers:", error);
      alert("An error occurred during submission. Please try again.");
    }
  };

  return (
    <>
      <div style={{ background: "#263F86", fontFamily: "Manrope-Regular" }}>
        <div className="custom-container">
          <Row>
            <Col md={10}>
              <h4 className="text-white mt-3">
                {assessment_name || "Assessment"}
              </h4>
              <p className="text-white">Attempt {attempts}</p>
            </Col>
            <Col md={2} className="mb-3">
              <Button
                type="button"
                className="btn btn-light w-100 h-75 fw-semibold mt-3"
                style={{ fontFamily: "Manrope-Regular" }}
                onClick={() => handleSubmit(false)}
                disabled={!isAllAnswered}
                aria-disabled={!isAllAnswered}
                title={
                  isAllAnswered
                    ? "Submit your completed assessment"
                    : `Answer all ${totalQuestions} questions before submitting`
                }
              >
                Submit for evaluation
              </Button>
            </Col>
          </Row>
        </div>
      </div>

      <div className="container" style={{ fontFamily: "Manrope-Regular" }}>
        <Row className="mb-2 align-items-center">
          <Col xs={6} md={6} className="d-flex justify-content-start">
            <h6 className="fw-bold mb-0">
              {answeredCount}/{totalQuestions} Answered
            </h6>
          </Col>

          {!loading && attemptTiming && (
            <Col
              xs={6}
              md={6}
              className="d-flex justify-content-end align-items-center"
            >
              <p className="fw-semibold spacing mb-0 me-2">Time Left:</p>
              <CountdownTimer
                duration={attemptTiming.remainingSeconds}
                totalDuration={attemptTiming.totalSeconds}
                warningThreshold={60}
                onWarning={() => setShowWarning(true)}
                onTimeUp={() => handleSubmit(true)}
              />
            </Col>
          )}
        </Row>
      </div>

      {showWarning && (
        <div
          className="container text-center"
          style={{ fontFamily: "Manrope-Regular" }}
        >
          <p className="text-danger fw-bold">
            Hurry up! Less than a minute remaining!
          </p>
        </div>
      )}

      <div className="container mt-3">
        <ExamQuestionNumbers
          onQuestionClick={setCurrentQuestionIndex}
          answeredQuestions={answeredQuestions}
          totalQuestions={questions}
        />
      </div>

      <ExamMcqQuestions
        currentQuestionIndex={currentQuestionIndex}
        setCurrentQuestionIndex={setCurrentQuestionIndex}
        setAnsweredQuestions={setAnsweredQuestions}
        setLoading={setLoading} // Pass setLoading to track loading state
        onAttemptTimingLoaded={setAttemptTiming}
        onQuestionsLoaded={setLoadedQuestionCount}
      />

      <Modal
        show={showSuccessModal}
        onHide={() => setShowSuccessModal(false)}
        centered
        style={{ fontFamily: "Manrope-Regular" }}
      >
        <Modal.Body className="text-center" style={{ padding: "80px" }}>
          <FaCircleCheck
            size={70}
            style={{ color: "#176B3A", marginBottom: "20px" }}
          />
          <h4 style={{ marginBottom: "10px", fontWeight: "bold" }}>
            Congratulations
          </h4>
          <p>You have successfully submitted your assessment.</p>
          <div
            style={{
              border: "2px solid #176B3A",
              borderRadius: "10px",
              padding: "20px",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              width: "300px",
              margin: "0 auto",
            }}
          >
            <div style={{ textAlign: "center" }}>
              <p
                style={{
                  fontSize: "14px",
                }}
              >
                Total Answered
              </p>
              <h3 style={{ fontSize: "30px", fontWeight: "bold" }}>
                {score.totalAnswered}
              </h3>
            </div>
            <div style={{ textAlign: "center" }}>
              <p
                style={{
                  fontSize: "14px",
                }}
              >
                Correct Answers
              </p>
              <h3 style={{ fontSize: "30px", fontWeight: "bold" }}>
                {score.correctAnswers}
              </h3>
            </div>
          </div>
          <Button
            variant="light"
            onClick={() => {
              setShowSuccessModal(false);
              navigate(-2);
            }}
            style={{
              marginTop: "20px",
              padding: "10px 20px",
              background: "#176B3A",
              color: "white",
            }}
          >
            Back to Home
          </Button>
        </Modal.Body>
      </Modal>
    </>
  );
}

export default ExamMcqPage;
